import { getFunctions, httpsCallable } from "firebase/functions";
import { isValidMapCarouselSlideId } from "@healthdesk/shared-utils";

/**
 * @param {unknown} err FirebaseError or unknown
 * @returns {string}
 */
export function formatMapCarouselDebitError(err) {
  const code = err && typeof err === "object" ? err.code : "";
  const msg =
    err && typeof err === "object" && typeof err.message === "string"
      ? err.message
      : "Could not complete billing for this click.";
  if (code === "functions/failed-precondition") {
    return msg.includes("Insufficient")
      ? msg
      : msg || "This ad cannot be billed right now. Try again later.";
  }
  if (code === "functions/not-found") {
    return "This listing is no longer on the map.";
  }
  if (code === "functions/invalid-argument") {
    return msg || "Invalid ad click request.";
  }
  if (code === "functions/unauthenticated") {
    return "Sign in to open sponsored links.";
  }
  return msg && !String(msg).startsWith("FirebaseError:") ? msg : "Billing check failed. Please try again.";
}

/**
 * Debits advertiser prepaid balance when CPC > 0. No-op when chargeCents is 0.
 * @param {{
 *   zip: string,
 *   listingDocId: string,
 *   slideId: string,
 *   chargeCents: number,
 * }} args
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
const MAP_CAROUSEL_TOPUP_MIN_USD = 5;
const MAP_CAROUSEL_TOPUP_MAX_USD = 5000;

/**
 * Redirect browser to Stripe Checkout to add prepaid CareMap carousel credits.
 * @param {{
 *   amountUsd: number,
 *   ownerKind: 'user' | 'facility',
 *   facilityId?: string,
 *   returnSegment: 'facility' | 'advertiser' | 'advertiser-budget',
 * }} args
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function startMapCarouselWalletTopUpCheckout({
  amountUsd,
  ownerKind,
  facilityId,
  returnSegment,
}) {
  const n = Number(amountUsd);
  if (
    !Number.isFinite(n) ||
    n < MAP_CAROUSEL_TOPUP_MIN_USD ||
    n > MAP_CAROUSEL_TOPUP_MAX_USD
  ) {
    return {
      ok: false,
      message: `Enter an amount between $${MAP_CAROUSEL_TOPUP_MIN_USD} and $${MAP_CAROUSEL_TOPUP_MAX_USD}.`,
    };
  }
  try {
    const fn = httpsCallable(getFunctions(), "createMapCarouselWalletTopUpCheckout");
    const res = await fn({
      amountUsd: n,
      ownerKind,
      facilityId: facilityId || undefined,
      returnSegment,
    });
    const url = res.data?.url;
    if (typeof url === "string" && /^https:\/\//i.test(url)) {
      window.location.assign(url);
      return { ok: true };
    }
    return { ok: false, message: "Checkout did not return a link. Try again." };
  } catch (e) {
    console.warn("startMapCarouselWalletTopUpCheckout failed", e);
    return { ok: false, message: formatMapCarouselDebitError(e) };
  }
}

/**
 * Debits prepaid balance for one CPM video impression. No-op when chargeCents is 0.
 * @param {{
 *   zip: string,
 *   listingDocId: string,
 *   slideId: string,
 *   chargeCents: number,
 * }} args
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function debitMapCarouselCpmImpressionIfNeeded({
  zip,
  listingDocId,
  slideId,
  chargeCents,
}) {
  const cents = Number(chargeCents) || 0;
  if (cents <= 0) {
    return { ok: true };
  }
  const z = String(zip || "").replace(/\D/g, "").slice(0, 5);
  const lid = String(listingDocId || "").trim();
  const sid = String(slideId || "").trim();
  if (!isValidMapCarouselSlideId(sid) || z.length !== 5 || !lid) {
    return { ok: false, message: "Ad data is incomplete; cannot record impression." };
  }
  const clientImpressionId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `imp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  try {
    const fn = httpsCallable(getFunctions(), "debitMapCarouselCpmImpression");
    await fn({ zip: z, listingDocId: lid, slideId: sid, clientImpressionId });
    return { ok: true };
  } catch (e) {
    console.warn("debitMapCarouselCpmImpression failed", e);
    return { ok: false, message: formatMapCarouselDebitError(e) };
  }
}

export async function debitMapCarouselCpcClickIfNeeded({
  zip,
  listingDocId,
  slideId,
  chargeCents,
}) {
  const cents = Number(chargeCents) || 0;
  if (cents <= 0) {
    return { ok: true };
  }
  const z = String(zip || "").replace(/\D/g, "").slice(0, 5);
  const lid = String(listingDocId || "").trim();
  const sid = String(slideId || "").trim();
  if (!isValidMapCarouselSlideId(sid) || z.length !== 5 || !lid) {
    return { ok: false, message: "Ad data is incomplete; cannot open link." };
  }
  const clientClickId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `clk_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  try {
    const fn = httpsCallable(getFunctions(), "debitMapCarouselCpcClick");
    await fn({ zip: z, listingDocId: lid, slideId: sid, clientClickId });
    return { ok: true };
  } catch (e) {
    console.warn("debitMapCarouselCpcClick failed", e);
    return { ok: false, message: formatMapCarouselDebitError(e) };
  }
}
